import React, { useState } from 'react';
import { Box, Button, Typography, useTheme, FormControlLabel, Checkbox } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Delete } from '@mui/icons-material';
import { useDarkMode } from '../context/DarkModeContext';
import TextInputArea from '../components/TextInputArea';
import JsonDiffReact from 'jsondiffpatch-react';
import Footer from '../components/Footer';

// Recursively normalize a value for order-insensitive comparison:
//   - Object keys are sorted alphabetically at every level
//   - Array elements are sorted by their canonical JSON string so that
//     [{"name":"Alice",...}, {"name":"John",...}] equals
//     [{"name":"John",...}, {"name":"Alice",...}]
function deepNormalize(value) {
  if (Array.isArray(value)) {
    const normalizedItems = value.map(deepNormalize);
    return normalizedItems.sort((a, b) => {
      const sa = JSON.stringify(a) ?? '';
      const sb = JSON.stringify(b) ?? '';
      return sa < sb ? -1 : sa > sb ? 1 : 0;
    });
  }
  if (value !== null && typeof value === 'object') {
    return Object.keys(value).sort().reduce((acc, key) => {
      acc[key] = deepNormalize(value[key]);
      return acc;
    }, {});
  }
  return value;
}

const JsonComparePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [leftJson, setLeftJson] = useState('');
  const [rightJson, setRightJson] = useState('');
  const [error, setError] = useState('');
  const [showDiff, setShowDiff] = useState(false);
  const [parsedLeft, setParsedLeft] = useState(null);
  const [parsedRight, setParsedRight] = useState(null);
  const [isIdentical, setIsIdentical] = useState(false);
  const [identicalMsg, setIdenticalMsg] = useState('');
  // When unchecked (default): order-insensitive. When checked: order-sensitive (original behaviour).
  const [orderSensitive, setOrderSensitive] = useState(false);

  const handleCompare = () => {
    setError('');
    setShowDiff(false);
    setIsIdentical(false);
    setIdenticalMsg('');
    try {
      if (!leftJson.trim() || !rightJson.trim()) {
        setError('Please enter JSON in both boxes.');
        return;
      }
      const rawLeft  = JSON.parse(leftJson);
      const rawRight = JSON.parse(rightJson);

      // Normalize: when order-insensitive, deep-sort all object keys before comparing/diffing
      const left  = orderSensitive ? rawLeft  : deepNormalize(rawLeft);
      const right = orderSensitive ? rawRight : deepNormalize(rawRight);

      setParsedLeft(left);
      setParsedRight(right);

      if (JSON.stringify(left) === JSON.stringify(right)) {
        setIsIdentical(true);
        setIdenticalMsg(
          orderSensitive
            ? 'Both JSON objects have the same structure and values.'
            : 'Both JSON objects have the same keys and values (key order ignored).'
        );
      } else {
        setShowDiff(true);
      }
    } catch {
      setError('Invalid JSON in one or both boxes.');
    }
  };

  const handleClear = () => {
    setLeftJson('');
    setRightJson('');
    setShowDiff(false);
    setError('');
    setParsedLeft(null);
    setParsedRight(null);
    setIsIdentical(false);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: theme.palette.background.default, 
      color: theme.palette.text.primary, 
      p: 3,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Dark mode toggle in top right corner */}
      <button
        onClick={toggleDarkMode}
        className="dark-mode-toggle"
        style={{ 
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 1000,
          color: '#fff',
          fontSize: '1rem',
          padding: '8px 16px',
          borderRadius: '4px',
          backgroundColor: '#1976d2',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '40px'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#1565c0'}
        onMouseLeave={(e) => e.target.style.backgroundColor = '#1976d2'}
      >
        <span className="dark-mode-text">
          {darkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}
        </span>
        <span className="dark-mode-icon">
          {darkMode ? '☀️' : '🌙'}
        </span>
      </button>

      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        mb: 3,
        gap: { xs: 2, sm: 0 }
      }}>
        <img
          src="/favicon.png"
          alt="Home"
          onClick={() => navigate('/')}
          title="Go to Home"
          style={{ height: '60px', width: '60px', cursor: 'pointer', borderRadius: '8px', flexShrink: 0, transition: 'opacity 0.15s' }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.75'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        />
        <Typography variant="h4" sx={{ color: darkMode ? '#42a5f5' : '#1565c0', fontWeight: 'bold', textAlign: 'center', flexGrow: 1, fontSize: { xs: '2rem', sm: '2.5rem' } }}>
          JSON Compare
        </Typography>
      </Box>
      {/* Main Content */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <TextInputArea
            label={<span style={{ color: darkMode ? '#90caf9' : '#1976d2', fontWeight: 'bold' }}>Original JSON</span>}
            value={leftJson}
            onChange={setLeftJson}
            placeholder="Paste your first JSON here..."
            onCopy={() => handleCopy(leftJson)}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <TextInputArea
            label={<span style={{ color: darkMode ? '#90caf9' : '#1976d2', fontWeight: 'bold' }}>Modified JSON</span>}
            value={rightJson}
            onChange={setRightJson}
            placeholder="Paste your second JSON here..."
            onCopy={() => handleCopy(rightJson)}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={handleCompare} sx={{ bgcolor: '#1976d2', color: '#fff', fontSize: '1rem', textTransform: 'none', minWidth: 140, '&:hover': { bgcolor: '#1565c0' } }}>
            Compare
          </Button>
          <Button variant="outlined" onClick={handleClear} startIcon={<Delete />} sx={{ color: '#1976d2', borderColor: '#1976d2', background: 'transparent', textTransform: 'none', minWidth: 140, fontSize: '1rem', '&:hover': { background: 'rgba(25, 118, 210, 0.08)', borderColor: '#1565c0', color: '#1565c0' } }}>
            Clear
          </Button>
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={orderSensitive}
              onChange={(e) => setOrderSensitive(e.target.checked)}
              sx={{ color: '#1976d2', '&.Mui-checked': { color: '#1976d2' } }}
            />
          }
          label={
            <Typography sx={{ fontSize: '0.9rem', color: darkMode ? '#90caf9' : '#1565c0', fontWeight: 500 }}>
              Strict key order (compare keys in their original order)
            </Typography>
          }
        />
      </Box>
      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
      {isIdentical && (
        <Box sx={{ 
          mt: 2, 
          background: darkMode ? '#0d2b5e' : '#e3f2fd',
          borderRadius: 2, 
          p: 3,
          textAlign: 'center',
          border: `2px solid ${darkMode ? '#1976d2' : '#1976d2'}`
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              color: darkMode ? '#90caf9' : '#1565c0', 
              fontWeight: 'bold',
              fontSize: '1.2rem'
            }}
          >
            ✅ JSONs are identical!
          </Typography>
          <Typography
            sx={{
              color: darkMode ? '#90caf9' : '#1976d2',
              mt: 1,
              fontSize: '1rem'
            }}
          >
            {identicalMsg}
          </Typography>
        </Box>
      )}
      {showDiff && parsedLeft && parsedRight && (
        <Box sx={{ mt: 2, background: darkMode ? '#23272e' : '#f8f8f8', borderRadius: 2, p: 2 }}>
          <JsonDiffReact left={parsedLeft} right={parsedRight} show={true} annotated={false} />
        </Box>
      )}
      <Footer />
    </Box>
  );
};

export default JsonComparePage; 